import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export function DataTable({ children, caption }: { children: ReactNode; caption?: string }) {
  return (
    // `min-w-0` pozwala kontenerowi zwęzić się poniżej szerokości tabeli.
    //
    // `relative` nie jest ozdobą: nagłówki dla czytników ekranu używają klasy
    // `sr-only`, czyli pozycjonowania bezwzględnego. Bez bloku zawierającego
    // taki element trafia poza kontener przewijania, wymyka się przycinaniu
    // i rozciąga całą stronę — to była przyczyna poziomego przewijania na
    // telefonie w kontach, salach, oddziałach i rozliczeniach.
    <div className="relative min-w-0 max-w-full overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-left">
        {caption && <caption className="sr-only">{caption}</caption>}
        {children}
      </table>
    </div>
  )
}

export function TableHead({ columns }: { columns: (string | { label: string; srOnly?: true })[] }) {
  return (
    <thead className="bg-surface-container-low">
      <tr>
        {columns.map((column, index) => {
          const label = typeof column === 'string' ? column : column.label
          const srOnly = typeof column === 'object' && column.srOnly
          return (
            <th
              key={index}
              scope="col"
              className="px-6 py-4 text-[11px] font-bold uppercase tracking-wider text-on-surface-variant"
            >
              <span className={cn(srOnly && 'sr-only')}>{label}</span>
            </th>
          )
        })}
      </tr>
    </thead>
  )
}

export function TableBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-outline/10">{children}</tbody>
}

export function TableRow({
  children,
  muted = false,
}: {
  children: ReactNode
  /** Wyszarza wiersz — używane dla rekordów nieaktywnych. */
  muted?: boolean
}) {
  return (
    <tr
      className={cn('transition-colors hover:bg-surface-container-low/60', muted && 'opacity-55')}
    >
      {children}
    </tr>
  )
}

export function TableCell({
  children,
  className,
  align = 'left',
}: {
  children: ReactNode
  className?: string
  align?: 'left' | 'right'
}) {
  return (
    <td
      className={cn('px-6 py-4 align-middle text-sm', align === 'right' && 'text-right', className)}
    >
      {children}
    </td>
  )
}

export function EmptyRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-16 text-center text-sm text-on-surface-variant">
        {message}
      </td>
    </tr>
  )
}
